import type { StoreHit } from '@/lib/algolia';
import { devWarn } from '@/lib/utils/devLog';

export const HITS_PER_PAGE = 6;
export const DEBOUNCE_DELAY = 300;

export const buildAddressLines = (store: StoreHit, isJapan: boolean): string[] => {
  const street = store.street_en || store.street;
  const street2 = store.street2_en || store.street2;
  const city = store.city_en || store.city;
  const region = store.region_en || store.region || '';
  const postalCode = store.postal_code || '';

  if (isJapan) {
    return [`${postalCode} ${region} ${city},`, street, ...(street2 ? [street2] : [])];
  }

  return [street, ...(street2 ? [street2] : []), `${city}, ${region} ${postalCode}`.trim()];
};

export interface BoutiqueShippingAddress {
  firstName: string;
  lastName: string;
  streetAddress1: string;
  streetAddress2?: string;
  city: string;
  postalCode: string;
  country: string;
  countryArea?: string;
  phone?: string;
  companyName?: string;
}

export const buildBoutiqueShippingAddress = (store: StoreHit): BoutiqueShippingAddress => {
  const name = store.name_en || store.name;

  return {
    firstName: name,
    lastName: '',
    companyName: name,
    streetAddress1: store.street_en || store.street,
    streetAddress2: store.street2_en || store.street2 || undefined,
    city: store.city_en || store.city,
    postalCode: store.postal_code || '',
    country: store.country_code.toUpperCase(),
    countryArea: store.region_en || store.region || undefined,
    phone: store.phone || undefined,
  };
};

export interface FetchBoutiquesParams {
  countryCode: string;
  query: string;
  page: number;
  aroundLatLng?: string;
}

export interface FetchBoutiquesResult {
  stores: StoreHit[];
  totalHits: number;
}

export const fetchBoutiqueByStoreId = async (
  storeId: string,
  countryCode: string
): Promise<StoreHit | null> => {
  try {
    const params = new URLSearchParams({
      country: countryCode.toUpperCase(),
      limit: '1',
      mode: 'Default',
      storeIds: storeId,
      boutiquePickup: 'true',
    });

    const response = await fetch(`/api/stores?${params.toString()}`);

    if (!response.ok) {
      devWarn('[BoutiquePickup] store lookup API error:', response.status);
      return null;
    }

    const json = await response.json();
    return json.data?.stores?.[0] ?? null;
  } catch (err) {
    devWarn('[BoutiquePickup] store lookup error:', err);
    return null;
  }
};

export const fetchBoutiqueStores = async ({
  countryCode,
  query,
  page,
  aroundLatLng,
}: FetchBoutiquesParams): Promise<FetchBoutiquesResult | null> => {
  try {
    const params = new URLSearchParams({
      country: countryCode.toUpperCase(),
      limit: String(HITS_PER_PAGE),
      mode: 'Default',
      q: query,
      boutiquePickup: 'true',
      page: String(page),
    });

    if (aroundLatLng) {
      params.set('aroundLatLng', aroundLatLng);
    }

    const response = await fetch(`/api/stores?${params.toString()}`);

    if (!response.ok) {
      devWarn('[BoutiquePickup] API error:', response.status);
      return null;
    }

    const json = await response.json();

    return {
      stores: json.data?.stores ?? [],
      totalHits: json.data?.totalHits ?? 0,
    };
  } catch (err) {
    devWarn('[BoutiquePickup] fetch error:', err);
    return null;
  }
};
